import React, { useState } from 'react';
import { Box, Typography, Paper, Button, Alert, Collapse, Accordion, AccordionSummary, AccordionDetails, AppBar, Toolbar, IconButton } from '@mui/material';
import { OpenInNew, CloudUpload, Info, Keyboard, ExpandMore, ArrowBack } from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';

const CAD_FULL_URL = '/cad/';
const CAD_SIMPLE_URL = 'https://mlightcad.github.io/cad-simple-viewer-example/';

const CadViewerPage: React.FC = () => {
  const [showEmbedded, setShowEmbedded] = useState(false);
  const navigate = useNavigate();

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Minimalny topbar – logo UNITRESCO i przycisk wstecz */}
      <AppBar position="static" elevation={0} sx={{ backgroundColor: 'background.paper', color: 'text.primary', borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar sx={{ minHeight: 48, px: 1 }}>
          <IconButton size="small" onClick={() => navigate(-1)} sx={{ mr: 1 }} aria-label="Wstecz">
            <ArrowBack />
          </IconButton>
          <Box component={Link} to="/" sx={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: "'Roboto', sans-serif", fontSize: 16, fontWeight: 600, color: '#c53030', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
              UNITRESCO
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      <Box sx={{ flex: 1, p: 2, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Przeglądarka CAD (DWG/DXF)
        </Typography>
        <Button
          variant="contained"
          startIcon={<CloudUpload />}
          href={CAD_FULL_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Otwórz przeglądarkę (zalecane)
        </Button>
        <Button
          variant="outlined"
          onClick={() => setShowEmbedded((v) => !v)}
        >
          {showEmbedded ? 'Ukryj podgląd w stronie' : 'Pokaż podgląd w stronie'}
        </Button>
        <Button variant="outlined" href={CAD_SIMPLE_URL} target="_blank" rel="noopener noreferrer">
          Wersja uproszczona
        </Button>
      </Box>

      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Keyboard sx={{ mr: 1, verticalAlign: 'middle' }} />
          <Typography>Skróty i komendy mlightcad</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box component="div" sx={{ '& table': { borderCollapse: 'collapse', width: '100%' }, '& th, & td': { border: '1px solid', borderColor: 'divider', p: 1, textAlign: 'left' } }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Nawigacja (trackpad Mac)</Typography>
            <ul style={{ margin: '0 0 8px 0', paddingLeft: 20 }}>
              <li><strong>Zoom:</strong> pinch (szczypanie dwoma palcami)</li>
              <li><strong>Pan:</strong> przeciągnięcie dwoma palcami (scroll, bez szczypania)</li>
              <li><strong>Pan (mysz):</strong> środkowy przycisk + przeciągnięcie</li>
            </ul>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Komendy (po załadowaniu pliku wpisz w linii komend)</Typography>
            <table>
              <thead><tr><th>Komenda</th><th>Opis</th></tr></thead>
              <tbody>
                <tr><td><code>pan</code></td><td>Przesunięcie widoku (przeciągnij)</td></tr>
                <tr><td><code>zoom</code></td><td>Przybliż do widoku całości</td></tr>
                <tr><td><code>zoomw</code></td><td>Przybliż do zaznaczonego prostokąta</td></tr>
                <tr><td><code>select</code></td><td>Zaznacz obiekty</td></tr>
                <tr><td><code>regen</code></td><td>Odśwież rysunek</td></tr>
                <tr><td><code>open</code></td><td>Otwórz plik</td></tr>
                <tr><td><code>whitebkcolor</code></td><td>Przełącz tło (białe/czarne)</td></tr>
              </tbody>
            </table>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Pełna wersja ma linię komend, menedżer warstw i więcej opcji. Wersja uproszczona to minimalne demo.
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Alert severity="info" icon={<Info />} sx={{ mb: 2 }}>
        <Typography variant="body2" component="span">
          <strong>Wskazówka:</strong> Aby uniknąć błędu „Kurza twarz!” (crash Chrome), otwórz przeglądarkę CAD w nowej karcie.
          Jeśli nadal występuje problem: zamknij inne karty, spróbuj pliku DXF zamiast DWG lub użyj przeglądarki Firefox.
        </Typography>
      </Alert>

      <Collapse in={showEmbedded}>
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            minHeight: 500,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            mb: 2,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 1 }}>
            Tryb osadzony – przy dużych plikach może wystąpić błąd. Użyj „Otwórz przeglądarkę” dla lepszej stabilności.
          </Typography>
          <iframe
            src={CAD_FULL_URL}
            title="Przeglądarka CAD"
            style={{
              width: '100%',
              height: 500,
              border: 0,
            }}
          />
        </Paper>
      </Collapse>

      {!showEmbedded && (
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            minHeight: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Kliknij „Otwórz przeglądarkę (zalecane)”, wybierz plik DWG lub DXF i przeglądaj zawartość.
            </Typography>
            <Button
              variant="contained"
              startIcon={<OpenInNew />}
              href={CAD_FULL_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Otwórz przeglądarkę CAD
            </Button>
          </Box>
        </Paper>
      )}
      </Box>
    </Box>
  );
};

export default CadViewerPage;
